import { push } from '@lagunovsky/redux-react-router'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import HomeAction from 'home/actions/HomeAction'
import { omit } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { POWER_SUPPLY_ID } from 'materiel/constants/MaterielConstants'
import { H_MAT_POWER_SUPPLY } from '../../../../account/constants/AccessRulesConstants'
import FilePanel from '../../../../components/file/FilePanel'
import SmallPicturePanel from '../../../../components/picture/SmallPicturePanel'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { getUser } from '../../../../utils/SettingUtils'
import { isSaveBlocked } from '../../../utils/MaterielUtils'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import PowerSupplyAction from '../actions/PowerSupplyAction'
import PowerSupplyPanel from './PowerSupplyPanel'

const PowerSupplyApp = () => {
    const {
        powerSupply: storePowerSupply,
        powerSupplyTypes,
        powerSupplySituations,
        powerSupplyPictures,
        powerSupplyFiles,
        powerSupplies,
        powerSuppliesLastSituations,
        materielSettingRules,
    } = useSelector(store => ({
        powerSupply: store.PowerSupplyReducer.powerSupply,
        powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
        powerSupplySituations: store.PowerSupplyReducer.powerSupplySituations,
        powerSupplyPictures: store.PowerSupplyReducer.powerSupplyPictures,
        powerSupplyFiles: store.PowerSupplyReducer.powerSupplyFiles,
        powerSupplies: store.PowerSupplyReducer.powerSupplies,
        powerSuppliesLastSituations: store.PowerSupplyReducer.powerSuppliesLastSituations,
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }))

    const dispatch = useDispatch()
    const params = useParams()

    const [powerSupply, setPowerSupply] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    const fetchPowerSupply = id => dispatch(PowerSupplyAction.fetchPowerSupply(id)).then(json => {
        if (json.id) {
            dispatch(PowerSupplyAction.fetchPowerSupplySituations(json.id))
            dispatch(PowerSupplyAction.fetchPowerSupplyPictures(json.id))
            dispatch(PowerSupplyAction.fetchPowerSupplyFiles(json.serialNumber))
            setPowerSupply({ ...json })
        }
    })

    const changeAdministrator = administrator => setPowerSupply(prev => ({ ...prev, administrator }))

    const changeStateAttribute = changes => setPowerSupply(prev => ({ ...prev, ...changes }))

    const deleteSituation = id => dispatch(PowerSupplyAction.deletePowerSupplySituation(id, params.id))

    const filterConsultant = actions => getUser().consultant === '1'
        ? omit(actions, ['save', 'edit', 'delete', 'replace'])
        : actions

    useActions(() => {
        updateMaterialize()
        if (params.id === 'new' && !storePowerSupply.id) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === POWER_SUPPLY_ID)
                    if (isSaveBlocked(powerSupplies, powerSupply, rules, 'powerSupplyType')) return

                    dispatch(PowerSupplyAction.createPowerSupply(powerSupply)).then(id => {
                        if (id) {
                            const situation = {
                                idPowerSupply: id,
                                situationDate: powerSupply.purchaseDate,
                                statusCode: 2,
                            }
                            dispatch(PowerSupplyAction.savePowerSupplySituation(situation))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push('/materiel/powerSupply'))
                },
            })
        }
        if (isEditMode) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === POWER_SUPPLY_ID)
                    if (isSaveBlocked(powerSupplies, powerSupply, rules, 'powerSupplyType')) return

                    dispatch(PowerSupplyAction.updatePowerSupply(powerSupply)).then(success => success && setIsEditMode(false))
                },
                cancel: () => {
                    setPowerSupply(storePowerSupply)
                    setIsEditMode(false)
                },
            })
        }
        return filterConsultant({
            edit: () => {
                setIsEditMode(true)
            },
            delete: () => {
                dispatch(PowerSupplyAction.deletePowerSupply(params.id))
            },
            replace: () => {
                dispatch(ToastrAction.info(i18n.inDeveloppmentFunctionnality))
            },
        })
    }, [isEditMode, powerSupply, storePowerSupply, powerSupplies, materielSettingRules])

    useTitle(() => {
        const { reference = '', serialNumber = '', powerSupplyType, id } = storePowerSupply
        const title = reference || serialNumber || id
        const type = (powerSupplyTypes.find(t => t.code === parseInt(powerSupplyType)) || {}).label || i18n.notDefined
        return ([
            {
                title: i18n.materiels,
                href: 'materiel',
            },
            {
                title: i18n.powerSupplies,
                href: 'materiel/powerSupply',
            },
            {
                title: type,
                href: `materiel/powerSupply/${params.id}`,
            },
            {
                title,
                href: `materiel/powerSupply/${params.id}`,
            },
        ])
    }, [storePowerSupply, powerSupplyTypes])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_POWER_SUPPLY)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return undefined
        }
        if (!powerSupplies.length) {
            dispatch(PowerSupplyAction.fetchPowerSupplies(true))
        }
        if (!powerSupplyTypes.length) {
            dispatch(PowerSupplyAction.fetchPowerSupplyTypes())
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        return () => dispatch(PowerSupplyAction.resetPowerSupply())
    }, [])

    useEffect(() => {
        if (params.id !== 'new') {
            fetchPowerSupply(params.id)
        } else {
            setIsEditMode(true)
        }
    }, [params.id])

    const getMaterielNumber = ({ serialNumber, reference }) => serialNumber || reference || ''

    const getPowerSupplyLabel = ps => {
        const number = getMaterielNumber(ps)
        if (ps.powerSupplyType) {
            const type = powerSupplyTypes.find(t => t.id === ps.powerSupplyType)
            if (type) {
                return `${type.label} - ${number}`
            }
        }
        return number
    }

    const getPowerSuppliesFormat = () => powerSupplies
        .filter(({ isEquipment }) => !isEquipment)
        .map(ps => ({
            id: ps.id,
            label: getPowerSupplyLabel(ps),
        }))

    const disabled = { active: isEditMode, disabled: !isEditMode }

    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={2}>
                    <Grid container size={9}>
                        <LastSituationMarkerPanel materielSituation={powerSupplySituations} />
                        <Grid container size={'grow'}>
                            <Grid size={12}>
                                <PowerSupplyPanel
                                    powerSupply={powerSupply}
                                    onChange={changeStateAttribute}
                                    {...disabled}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        <Stack spacing={1}>
                            <UpdatePanel
                                updateDate={storePowerSupply.updateDate}
                                updateLogin={storePowerSupply.updateLogin}
                                jobExecId={storePowerSupply.jobExecutionId}
                            />
                            <Card>
                                <CardContent>
                                    <AdministratorPanel
                                        onChange={changeAdministrator}
                                        selectedValue={powerSupply.administrator}
                                        isActive={!storePowerSupply.isEquipment && isEditMode}
                                    />
                                </CardContent>
                            </Card>
                            <AssigmentChartPanel materielSituation={powerSupplySituations} />
                            <SmallPicturePanel pictures={powerSupplyPictures} />
                            <FilePanel files={powerSupplyFiles} />
                        </Stack>
                    </Grid>
                    {
                        !storePowerSupply.isEquipment && (
                            <Grid size={12}>
                                <SituationPanel
                                    materielSituation={powerSupplySituations}
                                    active={isEditMode}
                                    idMateriel={storePowerSupply.id}
                                    serialNumber={storePowerSupply.serialNumber}
                                    purchaseDate={storePowerSupply.purchaseDate}
                                    idKey={'idPowerSupply'}
                                    lastSituations={powerSuppliesLastSituations}
                                    materials={getPowerSuppliesFormat()}
                                    saveFunction={situation => dispatch(PowerSupplyAction.savePowerSupplySituation(situation))}
                                    deleteFunction={deleteSituation}
                                />
                            </Grid>
                        )
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}

export default PowerSupplyApp
